/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { dateUtils } from '@opengeoweb/shared';
import { roundWithTimeStep } from '../../TimeSlider/timeSliderUtils';

export enum AnimationLengthMode {
  Auto = 'auto',
  Now = 'now',
  RecurringPeriod = 'recurringPeriod',
  ReferenceTime = 'referenceTime',
  Exact = 'exact',
}

export type TimeUnit =
  | 'years'
  | 'months'
  | 'weeks'
  | 'days'
  | 'hours'
  | 'minutes';

export const timeUnits: TimeUnit[] = [
  'years',
  'months',
  'weeks',
  'days',
  'hours',
  'minutes',
];

export interface RelativeTimeOffset {
  value: number;
  unit: TimeUnit;
}

export const maxOffsetByUnit: Record<TimeUnit, number> = {
  minutes: 999,
  hours: 999,
  days: 365,
  weeks: 99,
  months: 99,
  years: 99,
};

/** Shared by the menu and the slider handles, so neither can reach an offset the other rejects. */
export const clampOffset = ({
  value,
  unit,
}: RelativeTimeOffset): RelativeTimeOffset => {
  const maxOffset = maxOffsetByUnit[unit];
  return { value: Math.max(-maxOffset, Math.min(maxOffset, value)), unit };
};

export interface OffsetSelections {
  startOffset: RelativeTimeOffset;
  endOffset: RelativeTimeOffset;
}

export interface RecurringPeriodSelections extends OffsetSelections {
  /** Time of day in UTC as HH:mm. */
  anchorTime: string;
}

/** Exact names its instants outright rather than counting offsets from an anchor. */
export interface ExactSelections {
  animationStartTime: string;
  animationEndTime: string;
}

/**
 * Each mode keeps its own selections under a key named after it, so switching modes
 * cannot overwrite another mode's setup. The selected mode's own slice is required.
 */
export type AnimationLengthSetting =
  | {
      mode: AnimationLengthMode.Auto;
      now?: OffsetSelections;
      recurringPeriod?: RecurringPeriodSelections;
      referenceTime?: OffsetSelections;
      exact?: ExactSelections;
    }
  | {
      mode: AnimationLengthMode.Now;
      now: OffsetSelections;
      recurringPeriod?: RecurringPeriodSelections;
      referenceTime?: OffsetSelections;
      exact?: ExactSelections;
    }
  | {
      mode: AnimationLengthMode.RecurringPeriod;
      now?: OffsetSelections;
      recurringPeriod: RecurringPeriodSelections;
      referenceTime?: OffsetSelections;
      exact?: ExactSelections;
    }
  | {
      mode: AnimationLengthMode.ReferenceTime;
      now?: OffsetSelections;
      recurringPeriod?: RecurringPeriodSelections;
      referenceTime: OffsetSelections;
      exact?: ExactSelections;
    }
  | {
      mode: AnimationLengthMode.Exact;
      now?: OffsetSelections;
      recurringPeriod?: RecurringPeriodSelections;
      referenceTime?: OffsetSelections;
      exact: ExactSelections;
    };

export interface AnimationRange {
  animationStartTime: string;
  animationEndTime: string;
}

export const defaultStartOffset: RelativeTimeOffset = {
  value: 0,
  unit: 'hours',
};

export const defaultEndOffset: RelativeTimeOffset = { value: 2, unit: 'hours' };

export const defaultOffsetSelections: OffsetSelections = {
  startOffset: defaultStartOffset,
  endOffset: defaultEndOffset,
};

export const unitTranslationKey: Record<TimeUnit, string> = {
  years: 'timeslider-unit-years',
  months: 'timeslider-unit-months',
  weeks: 'timeslider-unit-weeks',
  days: 'timeslider-unit-days',
  hours: 'timeslider-unit-hours',
  minutes: 'timeslider-unit-minutes',
};

export const shortUnitTranslationKey: Record<TimeUnit, string> = {
  years: 'timeslider-unit-short-years',
  months: 'timeslider-unit-short-months',
  weeks: 'timeslider-unit-short-weeks',
  days: 'timeslider-unit-short-days',
  hours: 'timeslider-unit-short-hours',
  minutes: 'timeslider-unit-short-minutes',
};

export const modeTranslationKey: Record<AnimationLengthMode, string> = {
  [AnimationLengthMode.Auto]: 'timeslider-animation-length-auto',
  [AnimationLengthMode.Now]: 'timeslider-animation-length-now',
  [AnimationLengthMode.RecurringPeriod]:
    'timeslider-animation-length-recurring-period',
  [AnimationLengthMode.ReferenceTime]:
    'timeslider-animation-length-reference-time',
  [AnimationLengthMode.Exact]: 'timeslider-animation-length-exact',
};

type Duration = Parameters<typeof dateUtils.add>[1];

const offsetToDuration = ({ value, unit }: RelativeTimeOffset): Duration => ({
  [unit]: value,
});

const toAnimationTime = (date: Date): string =>
  dateUtils.dateToString(date, dateUtils.DATE_FORMAT_UTC)!;

/* The clock in the menu offers minutes in these increments, so an anchor must land on one. */
export const ANCHOR_MINUTE_STEP = 5;

const padded = (value: number): string => String(value).padStart(2, '0');

/**
 * Reads the UTC fields directly: `dateUtils.dateToString` formats a local twin of the
 * date, which a DST gap shifts by an hour.
 */
export const toAnchorTime = (date: Date): string => {
  const minutes = date.getUTCMinutes();
  return `${padded(date.getUTCHours())}:${padded(
    minutes - (minutes % ANCHOR_MINUTE_STEP),
  )}`;
};

/* Modes that count their offsets from an anchor instant rather than a stored range. */
const anchoredModes = [
  AnimationLengthMode.Now,
  AnimationLengthMode.RecurringPeriod,
  AnimationLengthMode.ReferenceTime,
];

export const isAnchoredMode = (mode?: AnimationLengthMode): boolean =>
  !!mode && anchoredModes.includes(mode);

/** Narrows the mode so the compiler can require an anchor time alongside it. */
export const isRecurringPeriodMode = (
  mode: AnimationLengthMode,
): mode is AnimationLengthMode.RecurringPeriod =>
  mode === AnimationLengthMode.RecurringPeriod;

const ANCHOR_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export const isValidAnchorTime = (anchorTime?: string): boolean =>
  !!anchorTime && ANCHOR_TIME_PATTERN.test(anchorTime);

const selectionsForMode = (
  setting: AnimationLengthSetting,
): OffsetSelections => {
  if (setting.mode === AnimationLengthMode.Now) {
    return setting.now;
  }
  if (setting.mode === AnimationLengthMode.RecurringPeriod) {
    return setting.recurringPeriod;
  }
  if (setting.mode === AnimationLengthMode.ReferenceTime) {
    return setting.referenceTime;
  }
  return defaultOffsetSelections;
};

/** The offsets of whichever mode is selected; the modes without offsets use the defaults. */
export const activeOffsets = (
  setting: AnimationLengthSetting,
): OffsetSelections => {
  const { startOffset, endOffset } = selectionsForMode(setting);
  return { startOffset, endOffset };
};

/** The unit the offsets are edited and dragged in. */
export const activeEditUnit = (setting: AnimationLengthSetting): TimeUnit =>
  selectionsForMode(setting).startOffset.unit;

export const defaultRecurringPeriodSelections = (
  now: Date = dateUtils.utc(),
): RecurringPeriodSelections => ({
  ...defaultOffsetSelections,
  anchorTime: toAnchorTime(now),
});

/** Presets carry settings the compiler cannot vouch for, so the selected mode is given the slice it needs. */
export const withModeSelections = (
  setting: AnimationLengthSetting,
  now: Date = dateUtils.utc(),
): AnimationLengthSetting => {
  if (setting.mode === AnimationLengthMode.Now) {
    return { ...setting, now: { ...defaultOffsetSelections, ...setting.now } };
  }
  if (setting.mode === AnimationLengthMode.RecurringPeriod) {
    const merged = {
      ...defaultRecurringPeriodSelections(now),
      ...setting.recurringPeriod,
    };
    return {
      ...setting,
      recurringPeriod: isValidAnchorTime(merged.anchorTime)
        ? merged
        : { ...merged, anchorTime: toAnchorTime(now) },
    };
  }
  if (setting.mode === AnimationLengthMode.Exact) {
    return {
      ...setting,
      exact: { ...defaultExactSelections(now), ...setting.exact },
    };
  }
  return setting;
};

/** What the anchored modes count their offsets from; each mode reads only its own input. */
export interface AnimationAnchorInputs {
  now?: Date;
  /** The leading layer's reference time, which only ReferenceTime counts from. */
  referenceTime?: string;
  /** In minutes; the clock is floored onto it, as the animation frames are. */
  timeStep?: number;
}

/* A longer timestep floors to the hour, so a window keeps the frames its offsets ask for. */
const MAX_ANCHOR_ROUNDING_IN_MINUTES = 60;

const startOfTimeStep = (instant: Date, timeStep: number): Date =>
  dateUtils.fromUnix(
    roundWithTimeStep(
      dateUtils.unix(instant),
      Math.min(timeStep, MAX_ANCHOR_ROUNDING_IN_MINUTES),
      'floor',
    ),
  );

/**
 * A recurring period anchors on its time of day within the current UTC date, so the
 * window it resolves rolls onto the next day once the clock passes midnight.
 */
const recurringPeriodAnchor = (anchorTime: string, now: Date): Date => {
  const [hours, minutes] = anchorTime.split(':').map(Number);
  return dateUtils.set(now, {
    hours,
    minutes,
    seconds: 0,
    milliseconds: 0,
  });
};

/** The instant a mode counts its offsets from. */
export const anchorInstantFor = (
  setting: AnimationLengthSetting,
  {
    now = dateUtils.utc(),
    referenceTime,
    timeStep,
  }: AnimationAnchorInputs = {},
): Date => {
  if (setting.mode === AnimationLengthMode.ReferenceTime && referenceTime) {
    return dateUtils.utc(referenceTime);
  }
  if (setting.mode === AnimationLengthMode.RecurringPeriod) {
    return recurringPeriodAnchor(setting.recurringPeriod.anchorTime, now);
  }
  return timeStep ? startOfTimeStep(now, timeStep) : now;
};

/**
 * Offsets are signed: negative lands before the anchor, positive after. They are
 * applied as calendar durations, so "+ 1 month" lands on the same day of the next
 * month rather than a fixed number of days later.
 */
export const resolveAnimationRange = (
  setting: AnimationLengthSetting,
  anchorInputs: AnimationAnchorInputs = {},
): AnimationRange => {
  const { startOffset, endOffset } = activeOffsets(setting);
  const anchor = anchorInstantFor(setting, anchorInputs);
  return {
    animationStartTime: toAnimationTime(
      dateUtils.add(anchor, offsetToDuration(startOffset)),
    ),
    animationEndTime: toAnimationTime(
      dateUtils.add(anchor, offsetToDuration(endOffset)),
    ),
  };
};

/* Calendar units have no fixed length, so this only seeds the search for the nearest offset. */
const approximateMinutesPerUnit: Record<TimeUnit, number> = {
  minutes: 1,
  hours: 60,
  days: 60 * 24,
  weeks: 60 * 24 * 7,
  months: 60 * 24 * 30,
  years: 60 * 24 * 365,
};

/** The whole-unit offset landing nearest the instant, so a handle reaches only what the menu can express. */
export const offsetForInstant = (
  anchor: Date,
  instant: Date,
  unit: TimeUnit,
): RelativeTimeOffset => {
  const estimate = Math.round(
    dateUtils.differenceInMinutes(instant, anchor) /
      approximateMinutesPerUnit[unit],
  );
  const distanceFromInstant = (value: number): number =>
    Math.abs(
      dateUtils.add(anchor, offsetToDuration({ value, unit })).getTime() -
        instant.getTime(),
    );
  const nearest = [estimate - 1, estimate, estimate + 1].reduce(
    (closest, value) =>
      distanceFromInstant(value) < distanceFromInstant(closest)
        ? value
        : closest,
  );
  return { value: nearest, unit };
};

/** Snaps a dragged handle onto the grid the mode's offsets can describe. */
export const snapInstantToOffsetGrid = (
  anchor: Date,
  instant: Date,
  unit: TimeUnit,
): Date =>
  dateUtils.add(
    anchor,
    offsetToDuration(clampOffset(offsetForInstant(anchor, instant, unit))),
  );

const isWithinOffsetLimit = ({ value, unit }: RelativeTimeOffset): boolean =>
  Math.abs(value) <= maxOffsetByUnit[unit];

/**
 * Recounts only the edges the range names, on the grid of the unit in use. An edge left
 * out keeps the offset it holds: reading its instant back would subtract whatever time
 * passed since that instant was resolved.
 */
const offsetsForRange = (
  setting: AnimationLengthSetting,
  { animationStartTime, animationEndTime }: Partial<AnimationRange>,
  anchorInputs: AnimationAnchorInputs,
): OffsetSelections | undefined => {
  const anchor = anchorInstantFor(setting, anchorInputs);
  const unit = activeEditUnit(setting);
  const heldOffsets = activeOffsets(setting);
  const offsetOf = (time: string): RelativeTimeOffset =>
    offsetForInstant(anchor, dateUtils.utc(time), unit);

  const startOffset = animationStartTime
    ? offsetOf(animationStartTime)
    : heldOffsets.startOffset;
  const endOffset = animationEndTime
    ? offsetOf(animationEndTime)
    : heldOffsets.endOffset;

  const movedBeyondReach =
    (!!animationStartTime && !isWithinOffsetLimit(startOffset)) ||
    (!!animationEndTime && !isWithinOffsetLimit(endOffset));

  return movedBeyondReach ? undefined : { startOffset, endOffset };
};

/**
 * A range set on the slider belongs to whichever mode is selected, so only that mode's
 * own selections change, and only on the edges the range names; the modes that read their
 * range from elsewhere keep it, and so does an edge the mode's offsets cannot reach.
 */
export const withDraggedRange = (
  setting: AnimationLengthSetting,
  range: Partial<AnimationRange>,
  anchorInputs: AnimationAnchorInputs = {},
): AnimationLengthSetting => {
  if (setting.mode === AnimationLengthMode.Exact) {
    return {
      ...setting,
      exact: {
        animationStartTime:
          range.animationStartTime ?? setting.exact.animationStartTime,
        animationEndTime:
          range.animationEndTime ?? setting.exact.animationEndTime,
      },
    };
  }
  const offsets = isAnchoredMode(setting.mode)
    ? offsetsForRange(setting, range, anchorInputs)
    : undefined;
  if (!offsets) {
    return setting;
  }
  if (setting.mode === AnimationLengthMode.RecurringPeriod) {
    return {
      ...setting,
      recurringPeriod: { ...setting.recurringPeriod, ...offsets },
    };
  }
  if (setting.mode === AnimationLengthMode.ReferenceTime) {
    return {
      ...setting,
      referenceTime: { ...setting.referenceTime, ...offsets },
    };
  }
  return { ...setting, now: { ...setting.now, ...offsets } };
};

/** Offsets can use different units, so validity is decided on the resolved instants. */
/** Exact names its range outright; every other mode resolves one from its offsets. */
export const settingRange = (
  setting: AnimationLengthSetting,
  anchorInputs: AnimationAnchorInputs = {},
): AnimationRange =>
  setting.mode === AnimationLengthMode.Exact
    ? setting.exact
    : resolveAnimationRange(setting, anchorInputs);

/* Exact times are picked to the minute, so seconds must not decide validity. */
export const isValidAnimationLength = (
  setting: AnimationLengthSetting,
  anchorInputs: AnimationAnchorInputs = {},
): boolean => {
  const { animationStartTime, animationEndTime } = settingRange(
    setting,
    anchorInputs,
  );
  return dateUtils.isBefore(
    dateUtils.utc(animationStartTime).setSeconds(0, 0),
    dateUtils.utc(animationEndTime).setSeconds(0, 0),
  );
};

/** Frames the animation may hold; the range is expanded one entry per time step. */
export const MAX_ANIMATION_STEPS = 10000;

/**
 * The cost of an animation is its span divided by the time step, so a wide range
 * is affordable with a coarse step and unaffordable with a fine one.
 */
export const isWithinAnimationStepLimit = (
  setting: AnimationLengthSetting,
  timeStepInMinutes: number | undefined,
  anchorInputs: AnimationAnchorInputs = {},
): boolean => {
  if (!timeStepInMinutes) {
    return true;
  }
  const { animationStartTime, animationEndTime } = settingRange(
    setting,
    anchorInputs,
  );
  const spanInMinutes = dateUtils.differenceInMinutes(
    dateUtils.utc(animationEndTime),
    dateUtils.utc(animationStartTime),
  );
  return Math.abs(spanInMinutes) / timeStepInMinutes <= MAX_ANIMATION_STEPS;
};

/* Shares the animation-time format, so a typed and a dragged range serialise alike. */
export const toExactAnimationTime = toAnimationTime;

/** A day either side of now, so an exact window opens on something usable. */
export const defaultExactSelections = (
  now: Date = dateUtils.utc(),
): ExactSelections => {
  const wholeMinute = dateUtils.set(now, { seconds: 0, milliseconds: 0 });
  return {
    animationStartTime: toExactAnimationTime(
      dateUtils.sub(wholeMinute, { days: 1 }),
    ),
    animationEndTime: toExactAnimationTime(
      dateUtils.add(wholeMinute, { days: 1 }),
    ),
  };
};

/** Starts Exact from whatever range is already on screen, falling back to the defaults. */
export const exactAnimationLengthFrom = (
  { animationStartTime, animationEndTime }: Partial<AnimationRange>,
  now: Date = dateUtils.utc(),
): AnimationLengthSetting => ({
  mode: AnimationLengthMode.Exact,
  exact:
    animationStartTime && animationEndTime
      ? { animationStartTime, animationEndTime }
      : defaultExactSelections(now),
});

const getOffsetFromTime = (
  time: string,
  anchorTime: Date,
): RelativeTimeOffset => {
  const relativeTime = dateUtils.utc(time);
  const value = dateUtils.differenceInMinutes(relativeTime, anchorTime);
  return {
    unit: 'minutes',
    value,
  };
};

export const getAnimationLengthFromRange = (
  mode: AnimationLengthMode,
  { animationStartTime, animationEndTime }: AnimationRange,
  now: Date = dateUtils.utc(),
): AnimationLengthSetting => {
  switch (mode) {
    case AnimationLengthMode.Now: {
      return {
        mode,
        now: {
          startOffset: getOffsetFromTime(animationStartTime, now),
          endOffset: getOffsetFromTime(animationEndTime, now),
        },
      };
    }
    case AnimationLengthMode.RecurringPeriod: {
      const today = dateUtils.startOfDayUTC(now);
      return {
        mode,
        recurringPeriod: {
          anchorTime: '00:00',
          startOffset: getOffsetFromTime(animationStartTime, today),
          endOffset: getOffsetFromTime(animationEndTime, today),
        },
      };
    }
    case AnimationLengthMode.Exact:
    default:
      return exactAnimationLengthFrom({ animationStartTime, animationEndTime });
  }
};
